import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { ProjectCost, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Drawer } from '../components/ui/Drawer'
import { Button } from '../components/ui/Button'
import { useProjectOptions } from '../hooks/useEntityOptions'
import { Badge } from '../components/ui/Badge'

const CATEGORIES = ['material', 'labor', 'subcontractor', 'equipment', 'other'] as const

export default function ProjectCosts() {
  const [costs, setCosts] = useState<ProjectCost[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedProject, setSelectedProject] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const { options: projectOptions } = useProjectOptions()
  const [formData, setFormData] = useState({
    project_slug: '',
    category: 'material' as string,
    description: '',
    vendor: '',
    quantity: '1',
    unit_cost: '',
    incurred_date: '',
    invoice_number: '',
  })

  const loadCosts = useCallback(async () => {
    if (!selectedProject) {
      setCosts([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const result = await api.get<PaginatedResult<ProjectCost>>(
        `/project-costs?project_slug=${selectedProject}&page=${page}&limit=20`
      )
      setCosts(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [selectedProject, page])

  useEffect(() => { loadCosts() }, [loadCosts])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/project-costs', {
        project_slug: formData.project_slug || selectedProject,
        category: formData.category,
        description: formData.description,
        vendor: formData.vendor || undefined,
        quantity: parseFloat(formData.quantity) || 1,
        unit_cost: parseFloat(formData.unit_cost) || 0,
        incurred_date: formData.incurred_date || undefined,
        invoice_number: formData.invoice_number || undefined,
      })
      setDrawerOpen(false)
      setFormData({ project_slug: '', category: 'material', description: '', vendor: '', quantity: '1', unit_cost: '', incurred_date: '', invoice_number: '' })
      loadCosts()
    } catch { /* handled */ }
    finally { setSaving(false) }
  }

  async function deleteCost(slug: string) {
    try {
      await api.delete(`/project-costs/${slug}`)
      loadCosts()
    } catch { /* handled */ }
  }

  const totalCost = costs.reduce((sum, c) => sum + (c.total_cost ?? 0), 0)

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Project Costs</h1>
        <Button onClick={() => { setFormData({ ...formData, project_slug: selectedProject }); setDrawerOpen(true) }}
          icon={<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>}
        >
          <span className="hidden sm:inline">Add Cost</span>
          <span className="sm:hidden">Add</span>
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <select
          value={selectedProject}
          onChange={(e) => { setSelectedProject(e.target.value); setPage(1) }}
          className="input max-w-xs"
          style={{ fontSize: 14 }}
        >
          <option value="">Select project...</option>
          {projectOptions.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
        {selectedProject && costs.length > 0 && (
          <span className="mono" style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
            Total: ${totalCost.toLocaleString()}
          </span>
        )}
      </div>

      {!selectedProject ? (
        <EmptyState title="Select a project" description="Choose a project to view its costs." />
      ) : loading ? (
        <TableSkeleton />
      ) : costs.length === 0 ? (
        <EmptyState
          title="No costs recorded"
          description="Add costs to track project expenses."
          action={<Button onClick={() => setDrawerOpen(true)}>Add Cost</Button>}
        />
      ) : (
        <>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Description</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Category</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left hidden sm:table-cell">Vendor</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Qty</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Unit Cost</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Total</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right hidden sm:table-cell">Date</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right"></th>
                </tr>
              </thead>
              <tbody>
                {costs.map((cost) => (
                  <tr key={cost.id}>
                    <td>
                      <div style={{ fontSize: 14, color: 'var(--text)' }}>{cost.description}</div>
                      {cost.invoice_number && <div className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>Inv# {cost.invoice_number}</div>}
                    </td>
                    <td>
                      <Badge v="info">{cost.category}</Badge>
                    </td>
                    <td className="hidden sm:table-cell" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{cost.vendor || '—'}</td>
                    <td className="text-right mono" style={{ fontSize: 14, color: 'var(--text)' }}>{cost.quantity}</td>
                    <td className="text-right mono" style={{ fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>${cost.unit_cost.toLocaleString()}</td>
                    <td className="text-right mono" style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>${cost.total_cost.toLocaleString()}</td>
                    <td className="text-right hidden sm:table-cell mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {cost.incurred_date ? new Date(cost.incurred_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="text-right">
                      <button onClick={() => deleteCost(cost.slug)} style={{ fontSize: 12, color: 'var(--error)' }} className="hover:underline">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Page {page} of {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="Add Cost">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Project *</label>
            <select
              value={formData.project_slug || selectedProject}
              onChange={(e) => setFormData({ ...formData, project_slug: e.target.value })}
              className="input"
              required
              style={{ fontSize: 14 }}
            >
              <option value="">Select project...</option>
              {projectOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Category *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="input"
              required
              style={{ fontSize: 14 }}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Description *</label>
            <input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input"
              required
              placeholder="e.g. Cat6 cable 1000ft"
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Vendor</label>
            <input
              value={formData.vendor}
              onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
              className="input"
              placeholder="e.g. ADI"
              style={{ fontSize: 14 }}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Quantity</label>
              <input
                type="number"
                step="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="input"
                style={{ fontSize: 14 }}
              />
            </div>
            <div>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Unit Cost ($) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.unit_cost}
                onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                className="input"
                required
                style={{ fontSize: 14 }}
              />
            </div>
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Date Incurred</label>
            <input
              type="date"
              value={formData.incurred_date}
              onChange={(e) => setFormData({ ...formData, incurred_date: e.target.value })}
              className="input"
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Invoice Number</label>
            <input
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              className="input"
              placeholder="INV-001"
              style={{ fontSize: 14 }}
            />
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%', fontSize: 14 }}>
            Add Cost
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
